import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ContextMenu, ContextMenuTrigger, ContextMenuTriggerProps } from 'react-contextmenu';
import { Link } from 'react-router';
import AccessibleElement from '../../common/accessible/accessibleElement';
import Portal from '../../../utilities/portal';
import { DragType } from '../../../models/dragTypes';
import Playlist, { addItems } from '../../../models/playlist/playlist';
import {
  isSyncedWithPlaylist,
  playlistSources,
  requestSync,
} from '../../../models/playlist/syncSourceHelpers';
import SyncType from '../../../models/playlist/syncType';
import PlaylistItem from '../../../models/playlistItem/playlistItem';
import { DropboxContext } from '../../../services/dropbox/dropboxService';
import { GoogleApiContext } from '../../../services/gapi/gapiService';
import createAddTracksToPlaylistThenLibrary from '../../../services/library/addTracksToPlaylistThenLibrary';
import { LibraryContext } from '../../../services/library/libraryService';
import { OneDriveContext } from '../../../services/oneDrive/oneDriveService';
import {
  SmallDeviceContext,
  SmallDeviceShow,
} from '../../../services/smallDevices/smallDeviceService';
import { SpotifyContext } from '../../../services/spotify/spotifyService';
import { SyncContext } from '../../../services/synchronization/syncService';
import { Context } from '../../../services/_globalContext/context';
import Constants, { Keys, messages } from '../../../settings';
import { preventDefaultStopPropogation } from '../../../utilities/preventDefault';
import FadilaMenuItem from '../../common/contextMenu/fadilaMenuItem';
import SyncIndicator from '../../common/syncIndicator/syncIndicator';
import { syncInfo } from '../../common/infoBubble/infoTexts';
import s from './playlistRow.module.scss';
import { baseSupportedDragTypes, handleDropCommon } from './playlistRowCommon';
import PlaylistMoveTarget from './playlistRowMoveTarget';
import PlaylistSyncMenu from './playlistSyncMenu';

interface Props {
  draggable: boolean;
  playlist: Playlist;
  selected: boolean;
  setCurPlaylist?(val: string): void;
  portalTarget?: HTMLElement;
}

export default React.memo(function PlaylistRow({
  draggable,
  playlist,
  selected,
  setCurPlaylist,
  portalTarget,
}: Props) {
  const inLibraryComponent = !!setCurPlaylist;

  const [supportedDropTypes] = useState<string[]>(
    [DragType.Playlist, DragType.PlaylistItems].concat(baseSupportedDragTypes),
  );

  const ctx = useContext(Context);
  const smallDevSvc = useContext(SmallDeviceContext);
  const libCtx = useContext(LibraryContext);
  const libCtxRef = useRef(libCtx);
  libCtxRef.current = libCtx;
  const spotifyCtx = useContext(SpotifyContext);
  const gapiCtx = useContext(GoogleApiContext);
  const oneDriveCtx = useContext(OneDriveContext);
  const dbxCtx = useContext(DropboxContext);
  const syncCtx = useContext(SyncContext);

  const syncedWithRemote = isSyncedWithPlaylist(playlist);
  // "YouTube and SoundCloud", when a playlist is synced with both.
  const syncedServices = Array.from(
    new Set(
      playlistSources(playlist).map((x) =>
        x.type === SyncType.SoundCloudPlaylist ? 'SoundCloud' : 'YouTube',
      ),
    ),
  ).join(' and ');
  const isSyncing = syncCtx.syncingPlaylistIds.has(playlist.id);

  const el = useRef<HTMLElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isHoverTop, setIsHoverTop] = useState(false);
  const [isHoverBottom, setIsHoverBottom] = useState(false);
  const [isHoverDrop, setIsHoverDrop] = useState(false);

  const pos = el.current?.getBoundingClientRect();

  const addTracksToPlaylistThenLibrary = createAddTracksToPlaylistThenLibrary(ctx, libCtx);

  function handleDrop(ev: React.DragEvent<HTMLLIElement>) {
    const dataTransfer: DataTransfer = ev.dataTransfer;
    const add = addItems(playlist, libCtx.updatePlaylist);

    const dragType = dataTransfer.getData('dragType');

    switch (dragType) {
      case DragType.Playlist:
        const id = dataTransfer.getData('text/plain');
        const pl = libCtx.playlists.find((pl) => pl.id === id);

        if (pl) {
          if (el.current) {
            const pos = el.current.getBoundingClientRect();

            if (ev.clientY < pos.top + pos.height / 3) {
              libCtx.movePlaylist(pl, playlist);
            } else if (ev.clientY > pos.top + (pos.height / 3) * 2) {
              libCtx.movePlaylist(pl, playlist, true);
            } else {
              add(pl.tracks.map(PlaylistItem.from));
            }
          }
        } else {
          ctx.callSnackbar(messages(Keys.playlistNotFound));
        }

        return;

      case DragType.PlaylistItems:
        const key = dataTransfer.getData('text/plain');
        const items = ctx.dndData[key] as { current: Iterable<PlaylistItem> };
        if (items) {
          add([...items.current].map(PlaylistItem.from));
        }

        return;
    }

    handleDropCommon(
      ctx,
      libCtx,
      gapiCtx,
      spotifyCtx,
      oneDriveCtx,
      dbxCtx,
      add,
      addTracksToPlaylistThenLibrary(add),
      ev,
    );
  }

  const onDragStart = (ev: React.DragEvent<HTMLLIElement>) => {
    console.debug('dragstart on playlist: ', playlist.id);

    // We set loads of data here since you can't call getData in onDragOver
    ev.dataTransfer.setData('dragType', DragType.Playlist);
    // types.includes checks for matching drag type in onDragOver
    ev.dataTransfer.setData(DragType.Playlist, DragType.Playlist);
    // onDragOver tests if we're hovering over the playlist that itself is being dragged
    // in that case we don't allow drop on yourself
    ev.dataTransfer.setData(playlist.id, playlist.id);
    ev.dataTransfer.setData('text/plain', playlist.id);

    ev.dataTransfer.effectAllowed = 'copyMove';

    setIsDragging(true);
  };
  const onDragOver = (ev: React.DragEvent<HTMLLIElement>) => {
    if (ev.dataTransfer.types.some((x) => supportedDropTypes.includes(x))) {
      if (!ev.dataTransfer.types.includes(playlist.id)) {
        ev.preventDefault();

        if (el.current) {
          const pos = el.current.getBoundingClientRect();

          if (ev.dataTransfer.types.includes(DragType.Playlist)) {
            if (ev.clientY < pos.top + pos.height / 3) {
              setIsHoverTop(true);
              setIsHoverBottom(false);
              setIsHoverDrop(false);
              ev.dataTransfer.dropEffect = 'move';
            } else if (ev.clientY > pos.top + (pos.height / 3) * 2) {
              setIsHoverTop(false);
              setIsHoverBottom(true);
              setIsHoverDrop(false);
              ev.dataTransfer.dropEffect = 'move';
            } else {
              setIsHoverTop(false);
              setIsHoverBottom(false);
              setIsHoverDrop(true);
              ev.dataTransfer.dropEffect = 'copy';
            }
          } else {
            setIsHoverDrop(true);
            ev.dataTransfer.dropEffect = 'copy';
          }
        }
      } else {
        ev.dataTransfer.dropEffect = 'none';
      }
    }
  };
  const onDragLeave = (_ev: React.DragEvent<HTMLLIElement>) => {
    console.debug('onDragLeave');
    setIsHoverTop(false);
    setIsHoverBottom(false);
    setIsHoverDrop(false);
  };
  const onDrop = (ev: React.DragEvent<HTMLLIElement>) => {
    console.debug('onDrop');

    // It's possible to drop invalid types when there's a nested html input element
    if (ev.dataTransfer.types.some((x) => supportedDropTypes.includes(x))) {
      if (!ev.dataTransfer.types.includes(playlist.id)) {
        ev.preventDefault();

        handleDrop(ev);

        setIsHoverTop(false);
        setIsHoverBottom(false);
        setIsHoverDrop(false);
      }
    }
  };

  function onDragEnd(_ev: React.DragEvent<HTMLLIElement>) {
    setIsDragging(false);
    // delete ctx.dndData[key];
  }

  function handleSubmit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();

    const form = ev.currentTarget;
    const inputEl: HTMLInputElement = form['playlist-name'];

    updatePlaylistName(inputEl);
  }

  /**
   * Escape cancels and keeps the old name. Leaving the box still commits -
   * a click elsewhere is a "yes, that name" - but the blur that follows a
   * cancel must not, and it arrives after the state that would tell it so,
   * hence the ref.
   */
  const cancelledRef = useRef(false);

  function handleBlur(ev: React.FocusEvent<HTMLInputElement>) {
    if (cancelledRef.current) {
      cancelledRef.current = false;

      return;
    }

    updatePlaylistName(ev.currentTarget);
  }

  function handleKeyDown(ev: React.KeyboardEvent<HTMLInputElement>) {
    if (ev.key === 'Escape') {
      ev.stopPropagation();
      cancelledRef.current = true;
      setIsRenaming(false);
    }
  }

  function updatePlaylistName(inputEl: HTMLInputElement) {
    const name = inputEl.value.trim();

    if (!name) {
      // A playlist with no name would be unfindable in the sidebar.
      ctx.callSnackbar('Playlist name missing');
    } else if (name !== playlist.name) {
      libCtx.updatePlaylist({ ...playlist, name });
    }

    setIsRenaming(false);
  }

  /**
   * Asking for a sync by hand also clears whatever the sync had given up on.
   * That is the manual retry: a video skipped because YouTube called it gone
   * gets one more attempt, and if it is still gone it is skipped again.
   */
  function triggerPlaylistSync() {
    libCtx.updatePlaylist(requestSync(playlist, true));
  }

  const inputEl = useRef<HTMLInputElement>(null);
  useEffect(
    function focusInput() {
      if (isRenaming) {
        inputEl.current?.focus();
      }
    },
    [isRenaming],
  );

  const onActivate = useCallback(
    function () {
      if (inLibraryComponent) {
        setCurPlaylist!(playlist.id);
        smallDevSvc.setState(SmallDeviceShow.Main);
      }
    },
    [inLibraryComponent, setCurPlaylist, smallDevSvc, playlist],
  );

  const contextTrigger = useRef<React.Component<ContextMenuTriggerProps, any, any>>(null);
  const toggleMenu = useCallback((e: React.MouseEvent) => {
    if (contextTrigger.current) {
      (contextTrigger.current as any).handleContextClick(e);
    }
  }, []);

  return (
    <AccessibleElement
      ref={el}
      onActivate={onActivate}

      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnter={(ev: React.DragEvent) => ev.preventDefault()}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}

      className={classNames(s.row, {
        [s.isDragging]: isDragging,
        [s.selected]: selected,
        [s.hoverDrop]: isHoverDrop,
        [s.spacedFlexContainer]: inLibraryComponent,
      })}
    >
      {(() => {
        const rowBody = (
          <>
            <ContextMenuTrigger
              ref={contextTrigger}
              id={playlist.id}
              holdToDisplay={-1}
              disableIfShiftIsPressed={true}
              attributes={{
                title: playlist.name,
                className: s.rowBody,
              }}
            >
              {playlist.id === Constants.LibraryId ? (
                <span className={s.playlistName}>{playlist.name}</span>
              ) : isRenaming ? (
                <form onSubmit={handleSubmit}>
                  <input
                    ref={inputEl}
                    id={`playlist-name-${playlist.id}`}
                    name="playlist-name"
                    type="text"
                    defaultValue={playlist.name}
                    className={s.playlistNameInput}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                  />
                </form>
              ) : (
                <>
                  <span className={s.playlistName}>{playlist.name}</span>
                  {syncedWithRemote && (
                    <SyncIndicator
                      active={isSyncing}
                      className={s.syncIndicator}
                      title={
                        isSyncing
                          ? `Syncing with ${syncedServices}`
                          : `Synced with ${syncedServices}`
                      }
                    />
                  )}
                </>
              )}
            </ContextMenuTrigger>
            <button className={classNames(s.menuBtn, 'hidden-control')} onClick={toggleMenu}>
              ☰
            </button>
          </>
        );

        return inLibraryComponent ? (
          rowBody
        ) : (
          <Link
            to={`/app/library/${playlist.id}`}
            className={classNames('no-styles-link', s.spacedFlexContainer)}
          >
            {rowBody}
          </Link>
        );
      })()}

      <Portal portalTarget={portalTarget}>
        <PlaylistMoveTarget
          hide={!(isHoverTop || isHoverBottom)}
          posY={isHoverTop ? pos?.top : pos?.bottom}
          width={pos?.width}
        />
        <ContextMenu id={playlist.id}>
          {playlist.id !== Constants.LibraryId && (
            <>
              <FadilaMenuItem
                onClick={preventDefaultStopPropogation(() => setIsRenaming(!isRenaming))}
              >
                {isRenaming && 'Cancel '}Rename
              </FadilaMenuItem>
              {playlist.syncSources.length > 0 && (
                <FadilaMenuItem
                  onClick={preventDefaultStopPropogation(triggerPlaylistSync)}
                  info={syncInfo.text}
                  infoLabel={syncInfo.label}
                >
                  Synchronize
                </FadilaMenuItem>
              )}
              <PlaylistSyncMenu playlist={playlist} />
              <FadilaMenuItem
                onClick={preventDefaultStopPropogation(() => libCtx.removePlaylist(playlist.id))}
                attributes={{
                  className: s.deleteMenuItem,
                }}
              >
                Delete
              </FadilaMenuItem>
            </>
          )}
        </ContextMenu>
      </Portal>
    </AccessibleElement>
  );
});
